'use client';

import React from 'react';
import { styled } from '@linaria/react';
import { useSpring, animated } from 'react-spring';

import { MAX_NUM_OF_LIKES } from '@/constants';

import { useContent } from '@/components/ContentProvider';

function LikeCount() {
  const { metrics } = useContent();
  const previousNumOfLikes = React.useRef(metrics.totalNumLikes);

  React.useEffect(() => {
    previousNumOfLikes.current = metrics.totalNumLikes;
  }, [metrics.totalNumLikes]);

  const action = getAction(
    metrics.totalNumLikes,
    metrics.totalNumLikesFromUser,
    previousNumOfLikes.current
  );

  const style = useSpring({
    from: {
      transform:
        action === 'increment'
          ? 'translateY(0px)'
          : action === 'decrement'
            ? 'translateY(-6px)'
            : 'translateY(0px) scale(0.65)',
      opacity: 1,
    },
    to: {
      transform:
        action === 'increment'
          ? 'translateY(-12px)'
          : action === 'decrement'
            ? 'translateY(6px)'
            : 'translateY(-5px) scale(1.25)',
      opacity: 0,
    },
    config: {
      tension: 100,
      friction: 40,
    },
    reset: true,
  });

  if (typeof metrics.totalNumLikes !== 'number') {
    return null;
  }

  const numOfLikes = metrics.totalNumLikes as number;

  return (
    <Wrapper
      data-has-been-liked-by-user={
        typeof metrics.totalNumLikesFromUser === 'number' &&
        metrics.totalNumLikesFromUser > 0
      }
    >
      <InnerWrapper>
        {numOfLikes.toLocaleString()}
        {action && (
          <LabelWrapper>
            <Label style={style}>{LABELS_BY_ACTION[action]}</Label>
          </LabelWrapper>
        )}
      </InnerWrapper>
    </Wrapper>
  );
}

const LABELS_BY_ACTION = {
  increment: '+1',
  decrement: '-1',
  'at-maximum': 'MAX',
};

const getAction = (
  totalNumLikes: number | null,
  totalNumLikesFromUser: number | null,
  previousNumOfLikes: number | null
) => {
  if (
    totalNumLikes === null ||
    previousNumOfLikes === null ||
    totalNumLikesFromUser === null
  ) {
    return null;
  }

  if (totalNumLikesFromUser === MAX_NUM_OF_LIKES) {
    return 'at-maximum';
  }

  return totalNumLikes > previousNumOfLikes
    ? 'increment'
    : 'decrement';
};

const Wrapper = styled.div`
  font-size: 0.9375rem;
  font-weight: var(--font-weight-bold);
  font-family: var(--font-family-mono);
  color: var(--color-gray-700);
  animation: fadeIn 400ms 200ms both;

  &[data-has-been-liked-by-user='true'] {
    color: var(--color-secondary);
  }
`;

const InnerWrapper = styled.span`
  position: relative;
  display: inline-block;
`;

const LabelWrapper = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  transform: translateX(calc(100% + 4px));
  pointer-events: none;
`;

const Label = styled(animated.div)`
  font-size: 0.6875rem;
  font-weight: var(--font-weight-bold);
  color: var(--color-gray-500);
  transform-origin: left center;
`;

export default React.memo(LikeCount);
